/**
 * Pure helpers for the header Inspector button (FRONT-692).
 *
 * Two readings, picked by the `eddy.ui-refactoring` flag (FRONT-3223):
 *
 * Flag on: the button is ONLY an on/off switch for the side panel (eddy,
 * 2026-10-26: "the inspector toggle in the top bar should have no activity
 * indicator, it's only a toggle"). Background work and waiting chats show in
 * the top bar's status indicator and window tabs, so this button says nothing
 * about activity, only whether the panel is open.
 *
 * Flag off: the old activity-aware button — amber with a pulsing dot while
 * background work runs or a chat waits, and a tooltip that says so
 * (`getInspectorTooltip`).
 */

/** The tooltip: what a click does. */
export function inspectorToggleLabel(open: boolean): string {
  return open ? 'Hide Inspector' : 'Show Inspector';
}

/** Icon colour: brand-lit while the panel is open, neutral otherwise. Never an activity colour. */
export function inspectorToggleColorClass(open: boolean): string {
  return open ? 'text-primary' : 'text-muted-foreground hover:text-foreground';
}

export interface InspectorTooltipInput {
  chatsNeedYou: boolean;
  inspectorActive: boolean;
  activeTaskCount: number;
}

/**
 * Flag off only. FRONT-662: a chat paused on a permission/question takes
 * precedence — it's the more urgent pull-back. Otherwise, if any background
 * work is active, surface the task count (singular/plural). Falls back to the
 * plain label.
 */
export function getInspectorTooltip({
  chatsNeedYou,
  inspectorActive,
  activeTaskCount,
}: InspectorTooltipInput): string {
  if (chatsNeedYou) {
    return 'A chat is waiting for you — open Inspector';
  }
  if (inspectorActive) {
    const taskWord = activeTaskCount === 1 ? 'task' : 'tasks';
    return `${activeTaskCount} ${taskWord} running — open Inspector`;
  }
  return 'Inspector';
}
